// Common error types for the application

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: Record<string, any>;
}

export interface AuthError extends ApiError {
  type: 'auth_error';
  field?: string; // Which field caused the error (email, password, etc.)
}

export interface DatabaseError extends ApiError {
  type: 'database_error';
  table?: string;
  operation?: 'select' | 'insert' | 'update' | 'delete';
}

export interface ValidationError extends ApiError {
  type: 'validation_error';
  field: string;
  value?: any;
}

export interface NetworkError extends ApiError {
  type: 'network_error';
  timeout?: boolean;
}

// Type guard functions to check error types
export const isApiError = (error: unknown): error is ApiError => {
  return typeof error === 'object' && error !== null && 'message' in error;
};

export const isAuthError = (error: unknown): error is AuthError => {
  return isApiError(error) && 'type' in error && error.type === 'auth_error';
};

export const isDatabaseError = (error: unknown): error is DatabaseError => {
  return isApiError(error) && 'type' in error && error.type === 'database_error';
};

export const isValidationError = (error: unknown): error is ValidationError => {
  return isApiError(error) && 'type' in error && error.type === 'validation_error';
};

export const isNetworkError = (error: unknown): error is NetworkError => {
  return isApiError(error) && 'type' in error && error.type === 'network_error';
};

// Utility function to create standardized error objects
export const createApiError = (
  message: string,
  options?: Partial<Omit<ApiError, 'message'>>
): ApiError => ({
  message,
  ...options,
});

export const createAuthError = (
  message: string,
  options?: Partial<Omit<AuthError, 'message' | 'type'>>
): AuthError => ({
  type: 'auth_error',
  message,
  ...options,
});